import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchActiveSocialLinks } from "../api/socialLinksApi";
import { subscribeNewsletter } from "../api/newsletterApi";
import { getSocialIcon } from "../utils/socialIcons";
import { toastError, toastSuccess } from "../utils/toast";

const EXPLORE = [
  { to: "/", label: "Home" },
  { to: "/#about", label: "About Us" },
  { to: "/plans/personal", label: "Services" },
  { to: "/contact", label: "Contact Us" },
];

export default function Footer() {
  const year = new Date().getFullYear();
  const [social, setSocial] = useState([]);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const data = await fetchActiveSocialLinks();
        if (on) setSocial(Array.isArray(data) ? data : []);
      } catch {
        if (on) setSocial([]);
      }
    })();
    return () => {
      on = false;
    };
  }, []);

  const onSubscribe = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || !/\S+@\S+\.\S+/.test(value)) {
      toastError("Please enter a valid email.");
      return;
    }

    setSaving(true);
    try {
      const res = await subscribeNewsletter(value);
      toastSuccess(res?.message || "Thanks for subscribing.");
      setEmail("");
    } catch (err) {
      toastError(err?.response?.data?.message || "Unable to subscribe right now.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <footer className="cl-footer">
      <div className="cl-container">
        <section className="cl-news">
          <div className="cl-news-card">
            <h3>Legal insights in your inbox</h3>
            <form className="cl-news-form" onSubmit={onSubscribe}>
              <div className="cl-input-wrapper">
                <input
                  className="cl-input"
                  type="email"
                  name="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={saving}
                />
                <button className="cl-subscribe" type="submit" disabled={saving}>
                  {saving ? "Saving..." : "Subscribe"}
                </button>
              </div>
            </form>
          </div>
        </section>

        <section className="cl-foot-top">
          <div className="cl-foot-col cl-foot-about">
            <h4>About</h4>
            <Link to="/" className="cl-logo">
              Click <span>Law</span>
            </Link>
            <p className="cl-foot-tagline">
            Click Law is a legal solutions platform operated by Seven Summit Ventures FZE, connecting clients with independent legal professionals and law firms.
            </p>
            {social.length > 0 && (
              <div className="cl-social">
                {social.map((item) => {
                  const Icon = getSocialIcon(item.platform);
                  return (
                    <a
                      key={item.id}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={item.label || item.platform}
                    >
                      <Icon />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <nav className="cl-foot-col cl-foot-nav" aria-label="Footer">
            <h4>Explore</h4>
            <ul className="cl-foot-links">
              {EXPLORE.map((item) => (
                <li key={item.label}>
                  {item.href ? (
                    <a href={item.href}>{item.label}</a>
                  ) : (
                    <Link to={item.to}>{item.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="cl-foot-col cl-foot-contact">
            <h4>Contact</h4>
            <p className="cl-contact-line">
              <span className="cl-ico" aria-hidden>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.09 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.32 1.77.6 2.6a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.48-1.17a2 2 0 0 1 2.11-.45c.83.28 1.7.48 2.6.6A2 2 0 0 1 22 16.92z" />
                </svg>
              </span>
              +971 58 590 0697
            </p>
            <p className="cl-contact-line">
              <span className="cl-ico" aria-hidden>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="m3 7 9 6 9-6" />
                </svg>
              </span>
              info@clicklaw.ae
            </p>
            <p className="cl-contact-line">
              <span className="cl-ico" aria-hidden>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" />
                  <circle cx="12" cy="10" r="2.5" />
                </svg>
              </span>
              Burjuman Business Tower, Dubai, United Arab Emirates
            </p>
          </div>

        </section>

        <div className="cl-foot-divider" />

        <div className="cl-foot-bottom">
          <div className="cl-foot-bottom-inner">
            <p>
              © {year} ClickLaw
              <span className="cl-foot-sep">·</span>
              <Link to="/refund-policy">Refund Policy</Link>
            </p>
            <p>Designed by Mingora Tech</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
